import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  ArrowRight, CheckCircle, Calendar, CreditCard, Sparkles, Loader2,
  TrendingUp, AlertTriangle, Target, Zap, X, Wallet,
} from "lucide-react";
import { toast } from "sonner";
import { InlineWidget } from "react-calendly";

const BUDGETS = ["Cost-Conscious", "Moderate", "Invest for ROI"];
const inputCls = "rounded-none bg-white border-navy/15 focus-visible:ring-gold focus-visible:ring-1 focus-visible:ring-offset-0 h-11";

export default function FreeAssessmentPage() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", business_name: "",
    industry: "", team_size: "", top_pain: "",
    budget_sensitivity: "Moderate", hourly_value: 75,
    hours_wasted_weekly: 0,
    monthly_tool_spend: 0,
    revenue_lever: "",
    extra_time_use: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    if (params.get("canceled") === "true") {
      toast("Checkout canceled. No charge made.", { description: "Feel free to book a free consult instead." });
    }
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.business_name) {
      toast.error("Name, email, and business name are required.");
      return;
    }
    if (!form.hours_wasted_weekly || form.hours_wasted_weekly <= 0) {
      toast.error("Tell us roughly how many hours/week your team wastes — we need it for the math.");
      return;
    }
    if (!form.monthly_tool_spend || form.monthly_tool_spend <= 0) {
      toast.error("Estimated monthly software spend is required.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/freemium/submit", form);
      setResult(r.data);
      setTimeout(() => document.getElementById("free-report")?.scrollIntoView({ behavior: "smooth" }), 200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  const buyNow = async (mode = "full") => {
    if (!result?.lead_id) return;
    setCheckingOut(mode);
    try {
      const path = mode === "plan" ? "/checkout/payment-plan" : "/checkout/assessment";
      const r = await api.post(path, {
        lead_id: result.lead_id,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Checkout failed");
      setCheckingOut(false);
    }
  };

  return (
    <div className="min-h-screen bg-ivory grain">
      <div className="h-[2px] bg-gold w-full" />
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center gap-3">
        <img src="/brand/logo.png" alt="Goldline" className="w-10 h-10 object-contain" />
        <div>
          <div className="font-display text-navy text-base font-semibold leading-none">Goldline Advisory</div>
          <div className="eyebrow text-navy/50 mt-1">Free AI Opportunity Scan</div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 pb-16">
        {!result ? (
          <div className="grid md:grid-cols-2 gap-12 pt-8">
            <div>
              <div className="eyebrow text-gold">3-Minute AI Audit</div>
              <h1 className="font-display text-5xl md:text-6xl text-navy mt-4 leading-[1.05]">
                See your<br /><span className="italic text-gold">5-section</span><br />AI report. Free.
              </h1>
              <div className="w-12 h-[2px] bg-gold mt-6" />
              <p className="mt-6 text-lg text-navy/70 max-w-md">
                Tell us nine things and we'll send you a deeper report: AI maturity score, industry benchmark,
                top 3 tools picked for you, a 1-week DIY quick win, and the annual cost of doing nothing.
              </p>
              <ul className="mt-8 space-y-3 text-sm text-navy/60">
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-gold" /> AI Maturity Score (1-10) vs. peers</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-gold" /> Annual hours + dollars you're losing</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-gold" /> 5-day plan you can execute Monday</li>
                <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-gold" /> 3 minutes · no credit card</li>
              </ul>
            </div>

            <form onSubmit={submit} className="bg-white border border-navy/10 p-8 md:p-10 space-y-5" data-testid="freemium-form">
              <div>
                <Label htmlFor="name">Your Name *</Label>
                <Input id="name" data-testid="free-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label htmlFor="email">Work Email *</Label>
                <Input id="email" data-testid="free-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label htmlFor="biz">Business Name *</Label>
                <Input id="biz" data-testid="free-business" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} required className={`mt-2 ${inputCls}`} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ind">Industry</Label>
                  <Input id="ind" data-testid="free-industry" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label htmlFor="team">Team Size</Label>
                  <Input id="team" data-testid="free-team" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} className={`mt-2 ${inputCls}`} />
                </div>
              </div>
              <div>
                <Label htmlFor="pain">What's eating the most hours each week?</Label>
                <Textarea id="pain" data-testid="free-pain" rows={3} value={form.top_pain} onChange={(e) => setForm({ ...form, top_pain: e.target.value })} className={`mt-2 ${inputCls} h-auto`} />
              </div>

              {/* Required strategic questions */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hwasted">Hours/wk wasted on admin tasks *</Label>
                  <Input id="hwasted" data-testid="free-hours-wasted" type="number" min={1} step={1}
                    value={form.hours_wasted_weekly || ""}
                    onChange={(e) => setForm({ ...form, hours_wasted_weekly: parseFloat(e.target.value) || 0 })}
                    required className={`mt-2 ${inputCls}`} />
                </div>
                <div>
                  <Label htmlFor="mspend">Monthly software spend ($) *</Label>
                  <Input id="mspend" data-testid="free-monthly-spend" type="number" min={1} step={1}
                    value={form.monthly_tool_spend || ""}
                    onChange={(e) => setForm({ ...form, monthly_tool_spend: parseFloat(e.target.value) || 0 })}
                    required className={`mt-2 ${inputCls}`} />
                </div>
              </div>
              <div>
                <Label htmlFor="rlever">Biggest revenue lever right now?</Label>
                <Input id="rlever" data-testid="free-revenue-lever" value={form.revenue_lever}
                  onChange={(e) => setForm({ ...form, revenue_lever: e.target.value })}
                  placeholder="More leads? Higher AOV? Faster delivery?" className={`mt-2 ${inputCls}`} />
              </div>
              <div>
                <Label htmlFor="extra">If you reclaimed 10 hrs/week, what would you spend them on?</Label>
                <Input id="extra" data-testid="free-extra-time" value={form.extra_time_use}
                  onChange={(e) => setForm({ ...form, extra_time_use: e.target.value })}
                  placeholder="New launches? Closing deals? Coaching team?" className={`mt-2 ${inputCls}`} />
              </div>

              <div>
                <Label>Hourly value of your time ($)</Label>
                <Input data-testid="free-hourly-value" type="number" min={1}
                  value={form.hourly_value || ""}
                  onChange={(e) => setForm({ ...form, hourly_value: parseFloat(e.target.value) || 0 })}
                  className={`mt-2 ${inputCls}`} />
              </div>

              <div>
                <Label>Budget Stance</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {BUDGETS.map((b) => (
                    <button type="button" key={b}
                      onClick={() => setForm({ ...form, budget_sensitivity: b })}
                      data-testid={`free-budget-${b}`}
                      className={`text-xs py-3 border ${form.budget_sensitivity === b ? "bg-navy text-ivory border-navy" : "bg-ivory text-navy border-navy/15 hover:border-gold"}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
              <Button type="submit" disabled={submitting} data-testid="free-submit"
                className="w-full h-12 bg-gold hover:bg-gold/90 text-navy rounded-none font-semibold">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating your 5-section report...</> : <>Get My Free Report <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
              <p className="text-[11px] text-navy/40 text-center">We'll email you the full report. No spam. Max 2 reports per IP per day.</p>
            </form>
          </div>
        ) : (
          <ReportView result={{ ...result, lead_name: form.name, lead_email: form.email }} onBuy={buyNow} checkingOut={checkingOut} />
        )}
      </div>

      <footer className="border-t border-navy/10 py-6 text-center eyebrow text-navy/40">
        Goldline Advisory Co. · goldlineadvisory.co
      </footer>
    </div>
  );
}

function ReportView({ result, onBuy, checkingOut }) {
  const r = result.report || {};
  const es = r.executive_snapshot || {};
  const bench = r.industry_benchmark || {};
  const qw = r.one_week_quick_win || {};

  return (
    <div id="free-report" className="max-w-3xl mx-auto pt-12 space-y-12" data-testid="freemium-report">
      <div>
        <div className="eyebrow text-gold">Your AI Snapshot Report</div>
        <h2 className="font-display text-4xl md:text-5xl text-navy mt-3 leading-[1.1]">{r.headline || "Your opportunity"}</h2>
        <div className="w-12 h-[2px] bg-gold mt-5" />
      </div>

      {/* SECTION 1: EXECUTIVE SNAPSHOT */}
      <section data-testid="report-section-1">
        <SectionHead num="01" title="Executive Snapshot" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Stat color="navy" big={`${es.maturity_score ?? 0}`} bigSuffix="/10" label="AI MATURITY" />
          <Stat color="ivory" big={(es.annual_hours_wasted || 0).toLocaleString()} label="HOURS WASTED / YEAR" />
          <Stat color="gold" big={`$${(es.annual_cost_of_inaction || 0).toLocaleString()}`} label="COST OF INACTION / YR" />
        </div>
        <div className="mt-5 bg-white border border-navy/10 p-5 flex items-start gap-3">
          <Target className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-navy font-semibold">{es.primary_leverage || ""}</div>
            <p className="text-sm text-navy/70 mt-1">{es.maturity_interpretation || ""}</p>
          </div>
        </div>
      </section>

      {/* SECTION 2: INDUSTRY BENCHMARK */}
      <section data-testid="report-section-2">
        <SectionHead num="02" title="Where You Stand" />
        <h3 className="font-display text-2xl text-navy">{bench.your_position || ""}</h3>
        <p className="mt-3 text-navy/70 text-base leading-relaxed">{bench.benchmark_summary || ""}</p>
        <div className="mt-4 border-l-4 border-gold bg-ivory/80 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
          <p className="font-display text-lg text-navy font-semibold">{bench.exposed_gap || ""}</p>
        </div>
      </section>

      {/* SECTION 3: TOP 3 TOOLS */}
      <section data-testid="report-section-3">
        <SectionHead num="03" title="Top 3 AI Wins for You" />
        <div className="space-y-3">
          {(r.top_3_tools || []).map((t, i) => (
            <div key={t.name || i} data-testid={`free-tool-${i}`}
              className="bg-white border border-navy/10 p-5 flex flex-col md:flex-row gap-5 items-start">
              <div className="font-display text-3xl text-gold w-12 flex-shrink-0">0{i + 1}</div>
              <div className="flex-1">
                <div className="eyebrow text-gold mb-1">{t.impact_tier || "Quick Win"}</div>
                <h4 className="font-display text-xl text-navy">{t.name}</h4>
                <p className="text-sm text-navy/70 mt-1 leading-relaxed">{t.why}</p>
                <div className="text-xs text-navy/50 mt-2 flex items-center gap-1">
                  <Zap className="w-3 h-3" /> {t.setup_minutes || 0} min setup
                </div>
              </div>
              <div className="font-display text-lg text-navy min-w-[90px] text-right md:text-right">
                ${t.monthly_cost}<span className="text-xs text-navy/50">/mo</span>
              </div>
            </div>
          ))}
        </div>
        {r.tools_paid_gap && (
          <p className="mt-3 text-xs italic text-navy/50 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-gold" /> {r.tools_paid_gap}
          </p>
        )}
        {r.wasted_spend_callout && (
          <p className="mt-1.5 text-xs italic text-navy/50 flex items-start gap-1.5">
            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0 text-gold" /> {r.wasted_spend_callout}
          </p>
        )}
      </section>

      {/* SECTION 4: 1-WEEK QUICK WIN */}
      <section data-testid="report-section-4">
        <SectionHead num="04" title="Your 1-Week DIY Quick Win" />
        <div className="bg-white border border-navy/10 p-6">
          <div className="eyebrow text-gold mb-1">SET UP THIS WEEK</div>
          <h3 className="font-display text-2xl text-navy">
            {qw.tool || ""}<span className="text-gold">.</span>
          </h3>
          <p className="text-sm text-navy/70 mt-2">{qw.outcome || ""}</p>
          <div className="mt-5 bg-ivory/80 p-5 space-y-3">
            {(qw.days || []).map((d, i) => (
              <div key={i} className="flex items-start gap-4 text-sm">
                <div className="font-display font-bold text-gold w-16 tracking-wider text-xs uppercase pt-0.5">
                  Day {d.day || i + 1}
                </div>
                <div className="text-navy flex-1">{d.action}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GOLDLINE DELIVERABLE — what we'd build for them */}
      {r.goldline_deliverable?.name && (
        <section data-testid="report-goldline-deliverable">
          <div className="bg-ivory border-l-4 border-gold p-7 md:p-9 relative overflow-hidden">
            <div className="absolute top-3 right-4 eyebrow text-gold/40 text-[10px]">▸ GOLDLINE BUILD</div>
            <div className="eyebrow text-gold">What Goldline Would Build For You</div>
            <h3 className="font-display text-3xl text-navy mt-2 leading-[1.1]">{r.goldline_deliverable.name}</h3>
            <p className="text-navy/70 mt-3 text-base leading-relaxed">{r.goldline_deliverable.description}</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-6">
              <div className="bg-white p-4 border border-navy/10">
                <div className="eyebrow text-gold text-[10px]">SETUP</div>
                <div className="font-display text-2xl text-navy mt-1">
                  {r.goldline_deliverable.setup_days || 5}<span className="text-sm text-navy/40 ml-1">days</span>
                </div>
              </div>
              <div className="bg-white p-4 border border-navy/10">
                <div className="eyebrow text-gold text-[10px]">EST. SAVINGS</div>
                <div className="font-display text-2xl text-navy mt-1">
                  ${(r.goldline_deliverable.estimated_monthly_savings || 0).toLocaleString()}<span className="text-sm text-navy/40">/mo</span>
                </div>
              </div>
              <div className="bg-white p-4 border border-navy/10 col-span-2 md:col-span-1">
                <div className="eyebrow text-gold text-[10px]">CONSOLIDATES</div>
                <div className="text-xs text-navy mt-1.5 leading-snug">
                  {(r.goldline_deliverable.consolidates || []).join(" · ") || "—"}
                </div>
              </div>
            </div>
            <p className="text-[11px] text-navy/50 mt-4 italic">
              ⚡ Goldline ships these in days, not months. Included in the Done-For-You engagement tier.
            </p>
          </div>
        </section>
      )}

      {/* SECTION 5: SALTY UPSELL */}
      <section data-testid="report-section-5">
        <div className="bg-navy text-ivory p-10">
          <div className="eyebrow text-gold">05 · What's In The Full $1,000 Assessment</div>
          <h3 className="font-display text-3xl mt-2">Here's what we left out.</h3>
          <p className="mt-4 text-ivory/70 text-base leading-relaxed">
            This free snapshot is sharp but intentionally incomplete. The full assessment is your
            done-for-you AI strategy deck — branded, defensible, and ready to show your team or board.
          </p>
          <ul className="mt-6 space-y-2.5">
            {(r.whats_missing || []).map((m, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-ivory/90">
                <X className="w-4 h-4 text-gold flex-shrink-0 mt-0.5" strokeWidth={3} />
                <span>{m}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-xs text-gold italic flex items-center gap-1.5">
            <TrendingUp className="w-3 h-3" /> 2-day deck turnaround. No homework on your end.
          </p>

          <div className="grid md:grid-cols-3 gap-3 mt-8">
            <button onClick={() => onBuy("full")} disabled={!!checkingOut} data-testid="buy-now-btn"
              className="bg-gold text-navy p-5 text-left hover:bg-gold/90 transition relative disabled:opacity-60">
              <CreditCard className="w-5 h-5 mb-3" />
              <div className="font-display text-lg font-bold leading-tight">Pay $1,000 once</div>
              <div className="text-xs text-navy/70 mt-1">Skip the call. 2-day turnaround.</div>
              <div className="mt-3 text-xs flex items-center font-semibold">
                {checkingOut === "full" ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Opening Stripe...</> : <>Pay full <ArrowRight className="w-3 h-3 ml-1" /></>}
              </div>
            </button>
            <button onClick={() => onBuy("plan")} disabled={!!checkingOut} data-testid="buy-plan-btn"
              className="bg-ivory/10 border-2 border-gold text-ivory p-5 text-left hover:bg-ivory/20 transition relative disabled:opacity-60">
              <Wallet className="w-5 h-5 mb-3 text-gold" />
              <div className="font-display text-lg font-bold leading-tight text-ivory">3 × $350/mo</div>
              <div className="text-xs text-ivory/70 mt-1">$1,050 total · auto-stops after 3 payments.</div>
              <div className="mt-3 text-xs flex items-center font-semibold text-gold">
                {checkingOut === "plan" ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Opening Stripe...</> : <>Pay over time <ArrowRight className="w-3 h-3 ml-1" /></>}
              </div>
            </button>
            <a href="#calendly-embed" data-testid="book-consult-btn"
              className="group bg-ivory/5 border border-ivory/30 p-5 text-left hover:bg-ivory/15 transition">
              <Calendar className="w-5 h-5 text-gold mb-3" />
              <div className="font-display text-lg font-bold leading-tight">Talk first (free)</div>
              <div className="text-xs text-ivory/60 mt-1">15-min walkthrough. No obligation.</div>
              <div className="mt-3 text-xs text-gold flex items-center">Pick a time below <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-1 transition" /></div>
            </a>
          </div>

          <p className="text-[11px] text-ivory/40 mt-6 flex items-center gap-2">
            <Sparkles className="w-3 h-3" /> {r.one_bold_next_step || "Your snapshot email is on its way."}
          </p>
        </div>
      </section>

      {/* CALENDLY INLINE EMBED — only show when calendly_url is set */}
      {result.calendly_url && (
        <section id="calendly-embed" data-testid="calendly-embed" className="bg-white border border-navy/10 -mt-px">
          <div className="px-8 pt-8 pb-2 text-center">
            <div className="eyebrow text-gold">Book Your Free 15-Min Walkthrough</div>
            <h3 className="font-display text-2xl text-navy mt-2">Pick a time that works.</h3>
            <p className="text-sm text-navy/60 mt-1">No prep needed — we'll review your report together and answer questions.</p>
          </div>
          <InlineWidget
            url={result.calendly_url}
            styles={{ height: "660px" }}
            pageSettings={{
              backgroundColor: "ffffff",
              primaryColor: "C69F56",
              textColor: "0E1A2B",
              hideEventTypeDetails: false,
              hideLandingPageDetails: false,
            }}
            prefill={{
              name: result?.lead_name,
              email: result?.lead_email,
            }}
          />
        </section>
      )}
    </div>
  );
}

function SectionHead({ num, title }) {
  return (
    <div className="mb-5">
      <div className="eyebrow text-gold">{num}</div>
      <h2 className="font-display text-3xl text-navy mt-1">{title}</h2>
      <div className="w-8 h-[2px] bg-gold mt-3" />
    </div>
  );
}

function Stat({ color, big, bigSuffix = "", label }) {
  const styles = {
    navy: "bg-navy text-ivory border-navy [&_.big]:text-gold",
    ivory: "bg-ivory text-navy border-navy/10 [&_.big]:text-navy",
    gold: "bg-gold text-navy border-gold [&_.big]:text-navy",
  };
  return (
    <div className={`p-5 text-center border ${styles[color]}`}>
      <div className="font-display text-4xl font-bold big">
        {big}{bigSuffix && <span className="text-base opacity-50 font-normal">{bigSuffix}</span>}
      </div>
      <div className="text-[10px] tracking-[0.18em] mt-2 opacity-80">{label}</div>
    </div>
  );
}
